import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { isSupabaseConfigured, supabase } from '../lib/supabase';
import type { AppRole, Profile } from '../types/hub';

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: AppRole[];
  loading: boolean;
  configMissing: boolean;
  refreshIdentity: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

async function loadIdentity(user: User | null): Promise<{ profile: Profile | null; roles: AppRole[] }> {
  if (!user || !supabase) return { profile: null, roles: [] };
  const [profileResult, rolesResult] = await Promise.all([
    supabase.from('profiles').select('*').eq('id', user.id).maybeSingle(),
    supabase.from('user_roles').select('role').eq('user_id', user.id),
  ]);
  if (profileResult.error) throw profileResult.error;
  if (rolesResult.error) throw rolesResult.error;
  return {
    profile: profileResult.data as Profile | null,
    roles: (rolesResult.data ?? []).map((item) => item.role as AppRole),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshIdentity = useCallback(async () => {
    if (!supabase) return;
    const { data } = await supabase.auth.getSession();
    setSession(data.session);
    const identity = await loadIdentity(data.session?.user ?? null);
    setProfile(identity.profile);
    setRoles(identity.roles);
  }, []);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    let active = true;
    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      setSession(data.session);
      try {
        const identity = await loadIdentity(data.session?.user ?? null);
        if (active) {
          setProfile(identity.profile);
          setRoles(identity.roles);
        }
      } finally {
        if (active) setLoading(false);
      }
    });

    const { data: subscription } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
      setSession(nextSession);
      try {
        const identity = await loadIdentity(nextSession?.user ?? null);
        if (active) {
          setProfile(identity.profile);
          setRoles(identity.roles);
        }
      } finally {
        if (active) setLoading(false);
      }
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut({ scope: 'local' });
    setSession(null);
    setProfile(null);
    setRoles([]);
    sessionStorage.clear();
  }, []);

  const value = useMemo<AuthState>(() => ({
    user: session?.user ?? null,
    session,
    profile,
    roles,
    loading,
    configMissing: !isSupabaseConfigured,
    refreshIdentity,
    signOut,
  }), [session, profile, roles, loading, refreshIdentity, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider.');
  return value;
}
